import { gql, useApolloClient, useMutation } from "@apollo/client";
import { useCallback } from "react";
import type {
  CubeMutation,
  CubeQuery,
  PackageWithLinksInput,
  PackageWithLinksResult,
  PopulationPage,
} from "@models/ISchema";
import packageWithLinksCrudMutation from "../../graphql/packageWithLinksCrud.mutation.graphql";
import packagePopulationsQuery from "../../graphql/packagePopulations.query";

const inferLinkCodeMutationDocument = gql`
  mutation packageCreationLinkCode($scenarioId: ID!, $originId: ID!, $courseId: ID!) {
    cube(scenarioId: $scenarioId, originId: $originId) {
      inferLinkCode(courseId: $courseId, index: 1)
    }
  }
`;

type PopulationsQueryData = {
  cube?: Pick<CubeQuery, "complementedPopulations">;
};

type InferLinkCodeMutationData = {
  cube?: Pick<CubeMutation, "inferLinkCode">;
};

type PackageWithLinksMutationData = {
  cube?: Pick<CubeMutation, "packageWithLinksCrud">;
};

export interface SearchPackagePopulationsParams {
  scenarioId: string;
  originId: string;
  termId: string;
  campusId?: string;
  modalityId?: string;
  programId?: string;
  page: number;
  searchTerm: string;
  pageSize?: number;
}

const EMPTY_POPULATION_PAGE: PopulationPage = { items: [] };

export const usePackageWithLeaguesCreationService = () => {
  const client = useApolloClient();
  const [inferLinkCodeMutation] = useMutation<InferLinkCodeMutationData>(
    inferLinkCodeMutationDocument,
  );
  const [packageWithLinksMutation] = useMutation<PackageWithLinksMutationData>(
    packageWithLinksCrudMutation,
  );

  const searchPopulations = useCallback(
    async ({
      scenarioId,
      originId,
      termId,
      campusId,
      modalityId,
      programId,
      page,
      searchTerm,
      pageSize = 20,
    }: SearchPackagePopulationsParams): Promise<PopulationPage> => {
      const { data } = await client.query<PopulationsQueryData>({
        query: packagePopulationsQuery,
        variables: {
          scenarioId,
          originId,
          termId,
          filter: {
            fields: {
              campusId: campusId ? { eq: campusId } : undefined,
              modalityId: modalityId ? { eq: modalityId } : undefined,
              programId: programId ? { eq: programId } : undefined,
            },
            pagination: { page, size: pageSize, searchTerm },
          },
        },
      });

      return data?.cube?.complementedPopulations ?? EMPTY_POPULATION_PAGE;
    },
    [client],
  );

  const inferLinkCode = useCallback(
    async (scenarioId: string, originId: string, courseId: string) => {
      const { data } = await inferLinkCodeMutation({
        variables: { scenarioId, originId, courseId },
      });
      return data?.cube?.inferLinkCode ?? null;
    },
    [inferLinkCodeMutation],
  );

  const submitPackageWithLinks = useCallback(
    async (
      scenarioId: string,
      originId: string,
      input: PackageWithLinksInput,
    ): Promise<PackageWithLinksResult | null> => {
      const { data } = await packageWithLinksMutation({
        variables: { scenarioId, originId, input },
      });
      return data?.cube?.packageWithLinksCrud ?? null;
    },
    [packageWithLinksMutation],
  );

  return { searchPopulations, inferLinkCode, submitPackageWithLinks };
};
